<template>
  <div v-if="show" class="modal-backdrop" @click.self="closeModal">
    <div class="modal-container">
      <div class="modal-content export-columns-modal">
        <div class="modal-header">
          <h2>{{ $t('exportColumns.title') }}</h2>
          <button @click="closeModal" class="btn btn-icon">
            <font-awesome-icon icon="fa-solid fa-xmark" />
          </button>
        </div>

        <div class="modal-body">
        <div class="columns-instructions">
          <p>{{ $t('exportColumns.instructions') }}</p>
        </div>

        <div class="columns-search">
          <input 
            v-model="searchQuery"
            type="text" 
            :placeholder="$t('exportColumns.search')"
            class="search-input"
          />
        </div>

        <div class="columns-actions">
          <button @click="selectAll" class="btn-secondary btn-sm">
            <font-awesome-icon icon="fa-solid fa-check-double" />
            {{ $t('exportColumns.selectAll') }}
          </button>
          <button @click="deselectAll" class="btn-secondary btn-sm">
            <font-awesome-icon icon="fa-solid fa-times" />
            {{ $t('exportColumns.deselectAll') }}
          </button>
          <button @click="resetToDefaults" class="btn-secondary btn-sm">
            <font-awesome-icon icon="fa-solid fa-rotate-left" />
            {{ $t('exportColumns.reset') }}
          </button>
        </div>

        <div class="type-filter-bar">
          <span class="type-filter-label">{{ $t('exportColumns.settingsFor') }}:</span>
          <div class="type-filter-buttons">
            <button
              @click="switchTypeFilter('purchase')"
              class="type-filter-btn"
              :class="{ active: activeTypeFilter === 'purchase' }"
            >
              {{ $t('exportColumns.typePurchase') }}
            </button>
            <button 
              @click="switchTypeFilter('sale')" 
              class="type-filter-btn"
              :class="{ active: activeTypeFilter === 'sale' }"
            >
              {{ $t('exportColumns.typeSale') }}
            </button>
          </div>
        </div>

        <div class="columns-list">
          <draggable 
            v-model="localColumns" 
            item-key="key"
            handle=".drag-handle"
            @end="onDragEnd"
          >
            <template #item="{element, index}">
              <div 
                v-if="matchesSearch(element)"
                class="column-item"
                :class="{ disabled: !element.enabled }"
              >
                <div class="drag-handle">
                  <font-awesome-icon icon="fa-solid fa-grip-vertical" />
                </div>
                
                <input 
                  type="number" 
                  class="column-order" 
                  :value="index + 1" 
                  min="1" 
                  :max="localColumns.length"
                  @change="onOrderChange(index, $event)"
                  @focus="$event.target.select()"
                />
                
                <label class="column-toggle">
                  <input 
                    type="checkbox" 
                    v-model="element.enabled"
                    @change="onColumnToggle"
                  />
                  <span class="toggle-track"></span>
                </label>
                
                <div class="column-info">
                  <div class="column-label">{{ translateColumnLabel(element.key, element.label) }}</div>
                  <div class="column-key">{{ element.key }}</div>
                </div>
              </div>
            </template>
          </draggable>
        </div>
      </div>

      <div class="modal-footer">
        <div class="footer-left">
          <div class="selected-count">
            {{ selectedCount }} / {{ totalCount }} {{ $t('exportColumns.columnsSelected') }}
          </div>
        </div>
        <div class="footer-right">
          <button @click="closeModal" class="btn-secondary">
            {{ $t('common.cancel') }}
          </button>
          <button @click="saveSettings" class="btn-primary" :disabled="saving">
            <font-awesome-icon v-if="saving" icon="fa-solid fa-spinner" spin />
            {{ saving ? $t('common.saving') : $t('common.save') }}
          </button>
        </div>
      </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import draggable from 'vuedraggable';
import axios from 'axios';

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  companyId: {
    type: Number,
    default: null
  },
  exportFormat: {
    type: String,
    default: 'excel'
  }
});

const emit = defineEmits(['close', 'saved']);
const { t, te } = useI18n();

const translateColumnLabel = (key, fallback) => {
  const i18nKey = `exportColumns.columns.${key}`;
  return te(i18nKey) ? t(i18nKey) : fallback;
};

const localColumns = ref([]);
const searchQuery = ref('');
const saving = ref(false);
const activeTypeFilter = ref('purchase');

// Computed
const selectedCount = computed(() => localColumns.value.filter(col => col.enabled).length);
const totalCount = computed(() => localColumns.value.length);

// Methods
const matchesSearch = (column) => {
  if (!searchQuery.value) return true;
  
  const query = searchQuery.value.toLowerCase();
  const translatedLabel = translateColumnLabel(column.key, column.label);
  return translatedLabel.toLowerCase().includes(query) || 
         column.label.toLowerCase().includes(query) || 
         column.key.toLowerCase().includes(query);
};

const loadColumns = async () => {
  try {
    const response = await axios.get('/api/export-column-settings', {
      params: {
        company_id: props.companyId,
        export_format: props.exportFormat,
        type_filter: activeTypeFilter.value
      }
    });
    
    localColumns.value = response.data.columns.map((col, index) => ({
      ...col,
      order: col.order ?? index + 1
    }));
  } catch (error) {
    console.error('Error loading columns:', error);
    // Load defaults
    await loadDefaultColumns();
  }
};

const loadDefaultColumns = async () => {
  try {
    const response = await axios.get('/api/export-column-settings/available', {
      params: {
        export_format: props.exportFormat
      }
    });
    
    localColumns.value = response.data.columns;
  } catch (error) {
    console.error('Error loading default columns:', error);
  }
};

const saveSettings = async () => {
  saving.value = true;
  
  try {
    // Update order based on current position
    const columnsToSave = localColumns.value.map((col, index) => ({
      key: col.key,
      label: col.label,
      enabled: col.enabled,
      order: index + 1
    }));
    
    await axios.post('/api/export-column-settings', {
      company_id: props.companyId,
      export_format: props.exportFormat,
      type_filter: activeTypeFilter.value,
      columns: columnsToSave
    });
    
    emit('saved', columnsToSave);
    closeModal();
  } catch (error) {
    console.error('Error saving column settings:', error);
    alert(t('exportColumns.saveFailed'));
  } finally {
    saving.value = false;
  }
};

const resetToDefaults = async () => {
  if (!confirm(t('exportColumns.resetConfirm'))) {
    return;
  }
  
  try {
    const response = await axios.post('/api/export-column-settings/reset', {
      company_id: props.companyId,
      export_format: props.exportFormat,
      type_filter: activeTypeFilter.value
    });
    
    localColumns.value = response.data.columns;
  } catch (error) {
    console.error('Error resetting columns:', error);
  }
};

const selectAll = () => {
  localColumns.value.forEach(col => {
    col.enabled = true;
  });
};

const deselectAll = () => {
  localColumns.value.forEach(col => {
    col.enabled = false;
  });
};

const onDragEnd = () => {
  // Reorder is handled automatically by draggable
  // Just need to update order numbers
  localColumns.value.forEach((col, index) => {
    col.order = index + 1;
  });
};

const onOrderChange = (currentIndex, event) => {
  let newIndex = parseInt(event.target.value) - 1;
  if (isNaN(newIndex) || newIndex < 0) newIndex = 0;
  if (newIndex >= localColumns.value.length) newIndex = localColumns.value.length - 1;
  
  const item = localColumns.value.splice(currentIndex, 1)[0];
  localColumns.value.splice(newIndex, 0, item);
  localColumns.value.forEach((col, i) => {
    col.order = i + 1;
  });
};

const onColumnToggle = () => {
  // Could add logic here if needed
};

const switchTypeFilter = async (type) => {
  activeTypeFilter.value = type;
  await loadColumns();
};

const closeModal = () => {
  emit('close');
};

// Watch for show prop changes
watch(() => props.show, (newVal) => {
  if (newVal) {
    activeTypeFilter.value = 'purchase';
    loadColumns();
    searchQuery.value = '';
  }
});
</script>

<style scoped>
/* Use global modal-backdrop and modal-container from app.css */
/* Custom styles only for specific modal content */

.export-columns-modal {
  /* Override global modal-content height to extend to max */
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  border-radius: 1rem;
  overflow: hidden;
}

.modal-header {
  /* Modal header is already styled globally, just add specific overrides */
  flex-shrink: 0;
  border-radius: 1rem 1rem 0 0;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
}

.modal-body {
  /* Override global modal-body to work with flex layout */
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-height: 0;
}

.columns-instructions {
  padding: 12px;
  background: var(--info-bg, #eff6ff);
  border-radius: 8px;
  border-left: 3px solid var(--info-border, #3b82f6);
}

.columns-instructions p {
  margin: 0;
  font-size: 14px;
  color: var(--text-secondary, #374151);
}

.columns-search {
  width: 100%;
}

.search-input {
  width: 100%;
  padding: 10px 14px;
  border: 1px solid var(--border-color, #d1d5db);
  border-radius: 8px;
  font-size: 14px;
}

.columns-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.type-filter-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  background: var(--bg-secondary, #f9fafb);
  border: 1px solid var(--border-color, #e5e7eb);
  border-radius: 8px;
}

.type-filter-label {
  font-size: 13px;
  font-weight: 500;
  color: var(--text-secondary, #6b7280);
  white-space: nowrap;
}

.type-filter-buttons {
  display: flex;
  gap: 4px;
}

.type-filter-btn {
  padding: 5px 14px;
  font-size: 13px;
  font-weight: 500;
  border: 1px solid var(--border-color, #d1d5db);
  border-radius: 6px;
  background: #fff;
  color: var(--text-primary, #374151);
  cursor: pointer;
  transition: all 0.15s;
}

.type-filter-btn:hover {
  background: #f3f4f6;
}

.type-filter-btn.active {
  background: var(--primary, #4f8cff);
  color: #fff;
  border-color: var(--primary, #4f8cff);
}

.btn-sm {
  padding: 6px 12px;
  font-size: 13px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.columns-list {
  flex: 1;
  overflow-y: auto;
  border: 1px solid var(--border-color, #e5e7eb);
  border-radius: 8px;
  padding: 8px;
  background: var(--bg-secondary, #f9fafb);
  min-height: 0;
}

.column-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: white;
  border: 1px solid var(--border-color, #e5e7eb);
  border-radius: 6px;
  margin-bottom: 8px;
  transition: all 0.2s;
}

.column-item:hover {
  border-color: var(--primary, #4f8cff);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.column-item.disabled {
  opacity: 0.5;
}

.drag-handle {
  cursor: grab;
  color: var(--text-tertiary, #9ca3af);
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
}

.drag-handle:active {
  cursor: grabbing;
}

.column-order {
  width: 40px;
  height: 32px;
  text-align: center;
  background: var(--bg-tertiary, #f3f4f6);
  border: 1px solid transparent;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  color: var(--text-secondary, #6b7280);
  padding: 0 4px;
  transition: border-color 0.15s ease;
  -moz-appearance: textfield;
}

.column-order::-webkit-outer-spin-button,
.column-order::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.column-order:focus {
  outline: none;
  border-color: var(--primary-color, #3b82f6);
  background: #fff;
}

.column-toggle {
  position: relative;
  display: inline-block;
  width: 36px;
  height: 20px;
  flex-shrink: 0;
  cursor: pointer;
}

.column-toggle input {
  opacity: 0;
  width: 0;
  height: 0;
  position: absolute;
}

.toggle-track {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #ccc;
  border-radius: 10px;
  transition: background 0.2s;
}

.toggle-track::before {
  content: '';
  position: absolute;
  width: 14px;
  height: 14px;
  left: 3px;
  bottom: 3px;
  background: white;
  border-radius: 50%;
  transition: transform 0.2s;
}

.column-toggle input:checked + .toggle-track {
  background: var(--primary, #4f8cff);
}

.column-toggle input:checked + .toggle-track::before {
  transform: translateX(16px);
}

.column-info {
  flex: 1;
  min-width: 0;
}

.column-label {
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary, #111827);
  margin-bottom: 2px;
}

.column-key {
  font-size: 12px;
  color: var(--text-tertiary, #9ca3af);
  font-family: monospace;
}

.modal-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
  border-top: 1px solid var(--border-color, #e5e7eb);
  background: var(--bg-secondary, #f9fafb);
  flex-shrink: 0;
  border-radius: 0 0 1rem 1rem;
}

.footer-left {
  flex: 1;
}

.selected-count {
  font-size: 13px;
  color: var(--text-secondary, #6b7280);
  font-weight: 500;
}

.footer-right {
  display: flex;
  gap: 12px;
}

.btn-secondary,
.btn-primary {
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.btn-secondary {
  background: white;
  border: 1px solid var(--border-color, #d1d5db);
  color: var(--text-primary, #374151);
}

.btn-secondary:hover {
  background: var(--hover-bg, #f3f4f6);
}

.btn-primary {
  background: var(--primary, #4f8cff);
  color: white;
}

.btn-primary:hover {
  background: var(--primary-dark, #3b7ad8);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
